import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PlayerDemo from '@/pages/detail/components/PlayerDemo';
import MovieCard from '@/components/feature/MovieCard';
import EmptyState from '@/components/feature/EmptyState';
import { useFavorites } from '@/hooks/useFavorites';
import { getCategoryName, getContentById, getRelated, formatHot } from '@/utils/content';

export default function Detail() {
  const { id = '' } = useParams();
  const item = getContentById(id);
  const { toggle, isFavorite } = useFavorites();

  const [selectedEpisode, setSelectedEpisode] = useState(1);
  const [copied, setCopied] = useState(false);
  const playerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSelectedEpisode(1);
    setCopied(false);
    if (item) {
      document.title = `${item.title}（${item.year}） - 中文字幕一区二区三区四虎在线`;
    }
  }, [id, item]);

  if (!item) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-16">
        <EmptyState
          icon="ri-error-warning-line"
          title="影视信息不存在"
          description="抱歉，未找到对应的影视详情，可能链接已失效或内容已下架。"
          secondaryLabel="返回首页"
          secondaryTo="/"
        />
      </div>
    );
  }

  const related = getRelated(item, 6);
  const isTvLike = item.episodes > 0;
  const favorited = isFavorite(item.id);

  const scrollToPlayer = () => {
    playerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleShare = async () => {
    const url = `${window.location.origin}${__BASE_PATH__ === '/' ? '' : __BASE_PATH__}/detail/${item.id}`;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
      } else {
        const input = document.createElement('input');
        input.value = url;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const episodeLabel = isTvLike ? `更新至 第 ${selectedEpisode} 集 / 全 ${item.episodes} 集` : `${item.duration} · ${item.quality}`;

  return (
    <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-8 md:py-10">
      <nav className="text-sm text-foreground-500 mb-4 flex flex-wrap items-center gap-1" aria-label="面包屑">
        <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
        <span className="mx-2">›</span>
        <Link to={`/category/${item.category}`} className="hover:text-primary-600 transition-colors cursor-pointer">
          {getCategoryName(item.category)}频道
        </Link>
        <span className="mx-2">›</span>
        <span className="text-foreground-900">{item.title}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
        <div className="lg:col-span-2">
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
            {item.title}
            <span className="ml-2 text-lg md:text-xl font-normal text-foreground-500">（{item.year}）</span>
          </h1>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-primary-100 text-primary-700 whitespace-nowrap">
              {getCategoryName(item.category)}
            </span>
            <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-accent-500 text-background-50 whitespace-nowrap">
              评分 {item.rating.toFixed(1)}
            </span>
            <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-secondary-100 text-secondary-900 whitespace-nowrap">
              {item.subtitle}
            </span>
            <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-background-200 text-foreground-700 whitespace-nowrap">
              {item.quality}
            </span>
            <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-background-200 text-foreground-700 whitespace-nowrap">
              热度 {formatHot(item.hot)}
            </span>
          </div>

          <div ref={playerRef} className="mt-6">
            <PlayerDemo
              cover={item.cover}
              title={item.title}
              episodeLabel={episodeLabel}
              quality={item.quality}
              subtitle={item.subtitle}
            />
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            {isTvLike ? (
              <button
                type="button"
                onClick={scrollToPlayer}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-play-fill text-lg"></i>
                播放第 {selectedEpisode} 集
              </button>
            ) : (
              <button
                type="button"
                onClick={scrollToPlayer}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-play-fill text-lg"></i>
                立即播放
              </button>
            )}

            <button
              type="button"
              onClick={() => toggle(item.id)}
              className={`inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-semibold border transition-colors cursor-pointer whitespace-nowrap ${
                favorited
                  ? 'bg-accent-500 text-background-50 border-accent-500'
                  : 'bg-background-50 text-foreground-800 border-background-300 hover:bg-background-200'
              }`}
            >
              <i className={`${favorited ? 'ri-bookmark-fill' : 'ri-bookmark-line'} text-lg`}></i>
              {favorited ? '已收藏' : '收藏'}
            </button>

            <button
              type="button"
              onClick={handleShare}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-semibold border border-background-300 text-foreground-800 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className={`${copied ? 'ri-check-line' : 'ri-share-line'} text-lg`}></i>
              {copied ? '链接已复制' : '分享'}
            </button>
          </div>

          {isTvLike && (
            <section className="mt-8">
              <h2 className="text-lg font-heading font-bold text-foreground-950 mb-3">剧集列表</h2>
              <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                {Array.from({ length: item.episodes }).map((_, i) => {
                  const ep = i + 1;
                  const active = ep === selectedEpisode;
                  return (
                    <button
                      key={ep}
                      type="button"
                      onClick={() => { setSelectedEpisode(ep); scrollToPlayer(); }}
                      className={`py-2 rounded-md text-sm transition-colors cursor-pointer ${
                        active
                          ? 'bg-primary-500 text-background-50 font-semibold'
                          : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                      }`}
                    >
                      {ep}
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          <section className="mt-8">
            <h2 className="text-lg font-heading font-bold text-foreground-950 mb-3">剧情简介</h2>
            <p className="text-sm md:text-base leading-relaxed text-foreground-700">{item.description}</p>
          </section>
        </div>

        <aside className="lg:col-span-1">
          <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
            <div className="w-full aspect-[2/3] rounded-md overflow-hidden bg-primary-900 mb-4">
              <img
                src={item.cover}
                alt={`${item.title}高清封面`}
                title={`${item.title} - 中文字幕在线观看`}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between gap-3">
                <dt className="text-foreground-500">类型</dt>
                <dd className="text-foreground-900 text-right">{getCategoryName(item.category)}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-foreground-500">题材</dt>
                <dd className="text-foreground-900 text-right">{item.genres.join(' / ')}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-foreground-500">地区</dt>
                <dd className="text-foreground-900 text-right">{item.region}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-foreground-500">年份</dt>
                <dd className="text-foreground-900 text-right">{item.year}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-foreground-500">字幕</dt>
                <dd className="text-foreground-900 text-right">{item.subtitle}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-foreground-500">{isTvLike ? '集数' : '时长'}</dt>
                <dd className="text-foreground-900 text-right">{isTvLike ? `全 ${item.episodes} 集` : item.duration}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-foreground-500">更新时间</dt>
                <dd className="text-foreground-900 text-right">{item.updatedAt}</dd>
              </div>
            </dl>
            <Link
              to={`/category/${item.category}`}
              className="mt-5 inline-flex w-full items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium bg-background-100 text-foreground-800 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-apps-2-line"></i>
              进入{getCategoryName(item.category)}频道
            </Link>
          </div>
        </aside>
      </div>

      <section className="mt-12 md:mt-16">
        <div className="flex items-end justify-between gap-4 mb-5">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">相关推荐</h2>
          <span className="text-sm text-foreground-500">同类型 · 同地区影视</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-5">
          {related.map((rel) => (
            <MovieCard key={rel.id} item={rel} />
          ))}
        </div>
      </section>
    </div>
  );
}